import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { InputNumber, Typography, Space, Button, message, Tag, Spin } from 'antd';
import { CalculatorOutlined, ExperimentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { theme } from '../../theme';
import { StandardCard } from '../common';

const { Title, Text, Paragraph } = Typography;

const CalculoSemillas: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [messageApi, contextHolder] = message.useMessage();
  const [kilosFibra, setKilosFibra] = useState<number | null>(null);
  const [coeficiente, setCoeficiente] = useState<number | null>(0);
  const [loading, setLoading] = useState(true);
  const [grabando, setGrabando] = useState(false);

  const calcularKilosSemilla = () => {
    if (kilosFibra === null || coeficiente === null || coeficiente === 0) return null;
    return kilosFibra * coeficiente;
  };

  // Cargar coeficiente desde la API
  useEffect(() => {
    const cargarCoeficiente = async () => {
      if (!user?.id_empresa) {
        setLoading(false);
        return;
      }
      if (!user?.id_sucursal) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        
        const response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${user.id_empresa}/${user.id_sucursal}/SEM`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        
        if (result && result.length > 0 && result[0].valor_numerico) {
          const valor = Number(result[0].valor_numerico);
          setCoeficiente(isNaN(valor) ? 0 : valor / 100);
        } else {
          setCoeficiente(0);
        }
      } catch (error) {
        console.error('Error al cargar coeficiente:', error);
        setCoeficiente(0);
        messageApi.error('Error al cargar el coeficiente');
      } finally {
        setLoading(false);
      }
    };

    cargarCoeficiente();
  }, [getAccessTokenSilently, user?.id_empresa, user?.id_sucursal]);

  // Función para grabar
  const grabarCalculo = async () => {
    if (kilosFibra === null || coeficiente === null || coeficiente === 0) {
      messageApi.warning('Complete todos los campos antes de grabar');
      return;
    }

    if (!user?.id_empresa || !user?.id_sucursal) {
      messageApi.error('No se pudo obtener la información de la empresa o sucursal');
      return;
    }

    let hide: (() => void) | undefined;
    try {
      setGrabando(true);
      hide = messageApi.loading('Grabando coeficiente...', 0);
      
      const token = await getAccessTokenSilently();
      
              const response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/grabar_datos_fijos/${user.id_empresa}/${user.id_sucursal}/SEM/a/${Math.round(coeficiente * 100)}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

      const result = await response.json();
      
      if (result.status === 'success') {
        hide?.();
        messageApi.success('Coeficiente grabado correctamente');
        
        // Reiniciar formulario
        setKilosFibra(null);
        setCoeficiente(0);
      } else {
        hide?.();
        messageApi.error(result.message || 'Error al grabar el coeficiente');
      }
    } catch (error) {
      console.error('Error al grabar:', error);
      hide?.();
      messageApi.error('Error de conexión al grabar el coeficiente');
    } finally {
      setGrabando(false);
    }
  };

  const kilosSemilla = calcularKilosSemilla();
  const coeficienteDisplay = coeficiente ?? 0;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <StandardCard
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalculatorOutlined />
            Cálculo de Semillas
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        <Paragraph style={{ color: theme.colors.textPrimary, marginBottom: '16px' }}>
          Define el coeficiente que relaciona los kilos de fibra producidos con los kilos de semilla estimados.
          Este valor se usa como referencia para los reportes de producción.
        </Paragraph>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Tag color="gold" style={{ padding: '6px 12px', fontSize: 14 }}>
            <InfoCircleOutlined />  Fórmula: kilos fibra × coeficiente
          </Tag>
          <Tag color="green" style={{ padding: '6px 12px', fontSize: 14 }}>
            {`Coeficiente actual: ${coeficienteDisplay.toFixed(2)}`}
          </Tag>
          <Tag color="blue" style={{ padding: '6px 12px', fontSize: 14 }}>
            {`Resultado estimado: ${kilosSemilla?.toFixed(2) ?? '--'} kg`}
          </Tag>
        </div>
        <Paragraph style={{ marginBottom: 0, color: theme.colors.textSecondary, fontSize: 13 }}>
          Nota: La fórmula utilizada para el cálculo de semilla es <Text strong>KILOS_FIBRA × COEFICIENTE</Text>.
          Un coeficiente muy utilizado es <Text code>1.4</Text>. En caso que sea <Text code>0</Text> la producción de semilla se mide y el dato se ingresa de otro modo.
        </Paragraph>
      </StandardCard>

      <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <StandardCard
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExperimentOutlined />
              Parámetros de cálculo
            </span>
          }
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ color: theme.colors.textPrimary }}>Kilos de fibra</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Ingrese kilos de fibra"
                value={kilosFibra}
                onChange={setKilosFibra}
                min={0}
                step={0.01}
                precision={2}
                size="large"
              />
            </div>

            <div>
              <Text strong style={{ color: theme.colors.textPrimary }}>Coeficiente (promedio histórico)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                placeholder={loading ? 'Cargando...' : 'Ingrese coeficiente'}
                value={coeficiente}
                onChange={setCoeficiente}
                min={0}
                step={0.1}
                precision={2}
                size="large"
                disabled={loading}
              />
            </div>

            <div style={{
              backgroundColor: theme.colors.backgroundGray,
              padding: '16px',
              borderRadius: theme.styles.borderRadius,
              border: `1px solid ${theme.colors.borderLight}`,
              textAlign: 'center'
            }}>
              <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary }}>Resultado estimado</Text>
              <div style={{ marginTop: 10 }}>
                {kilosSemilla !== null ? (
                  <Title level={2} style={{ margin: 0, color: theme.colors.primary }}>
                    {kilosSemilla.toFixed(2)} kg
                  </Title>
                ) : (
                  <Text type="secondary" style={{ fontSize: '16px' }}>
                    Ingrese los valores para calcular
                  </Text>
                )}
              </div>
              {kilosFibra !== null && coeficiente !== null && coeficiente !== 0 && (
                <Paragraph style={{ margin: 0, color: theme.colors.textSecondary }}>
                  {kilosFibra} kg × {coeficiente} = {kilosSemilla?.toFixed(2)} kg
                </Paragraph>
              )}
            </div>

            <Button
              type="primary"
              onClick={grabarCalculo}
              loading={grabando}
              size="large"
              style={{ width: '100%', fontWeight: 600 }}
            >
              Guardar coeficiente
            </Button>
          </Space>
        </StandardCard>

        <StandardCard
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InfoCircleOutlined />
              Referencias y recomendaciones
            </span>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Paragraph style={{ marginBottom: 0 }}>
              <Text strong>¿Para qué sirve?</Text><br />
              Este coeficiente alimenta los reportes de producción cuando no se dispone del peso real de semilla.
            </Paragraph>

            <Paragraph style={{ marginBottom: 0 }}>
              <Text strong>Valores sugeridos</Text><br />
              Históricamente se utilizan coeficientes entre <Text code>1.2</Text> y <Text code>1.5</Text> dependiendo del tipo de algodón.
            </Paragraph>

            <Paragraph style={{ marginBottom: 0 }}>
              <Text strong>Buenas prácticas</Text><br />
              Actualiza el coeficiente cada vez que finaliza una campaña y guarda un registro de los valores utilizados.
            </Paragraph>
          </Space>
        </StandardCard>
      </div>
    </>
  );
};

export default CalculoSemillas;
